import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Radio } from '@digital-twins/design-system/icons'
import { toast } from 'sonner'
import {
  SocialSyncSchedule,
  SocialUniverseItem,
  SocialWalletUsageSummary,
} from '@digital-twins/shared-types'
import { Heading } from '@/components/ui/Heading'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useCreditInfo } from '@/lib/hooks'
import { CONTACT_SALES_URL } from '@/pages/SettingsPage/components/billing/planConfig'
import { SocialUniverseCard } from './SocialUniverseCard'
import { SocialUniverseDrawer } from './SocialUniverseDrawer'
import { SocialUsageMeter } from './SocialUsageMeter'
import { useSocialListeningApi } from './SocialListeningApiContext'
import { socialLimitErrorToast } from './socialLimitErrors'
import { refreshSocialPostsUsage } from './useSocialPostsUsage'
import { useRefreshCreditsOnEdge } from './useRefreshCreditsOnEdge'

export interface SocialMediaTabProps {
  /** Hide the meter and "New topic" row. */
  showHeader?: boolean
  universes: Array<SocialUniverseItem>
  canManage: boolean
  syncSchedule: SocialSyncSchedule
  isLoading: boolean
  hasError?: boolean
  /** The org's posts this month; null hides the meter. */
  usage?: SocialWalletUsageSummary | null
  /** Shown instead of the meter (the back office's usage and allowance panel). */
  usagePanel?: ReactNode
  /**
   * The Topic open in the drawer (the back office). The app leaves it unset:
   * `onOpenUniverseChange` opens the Topic page there instead.
   */
  openUniverseToken?: string | null
  onOpenUniverseChange: (token: string | null) => void
  onUniverseChanged: (universe: SocialUniverseItem) => void
  onUniverseDeleted: (token: string) => void
  onRefresh: () => void
  /** Opens the New topic dialog; absent hides "New topic" (the back office). */
  onCreateTopic?: () => void
  /** Opens the Edit topic dialog; absent hides "Edit topic" (the back office). */
  onEditTopic?: (universe: SocialUniverseItem) => void
  /** False where hiding posts is not offered (the back office). Defaults to `canManage`. */
  canHidePosts?: boolean
  showRunDiagnostics?: boolean
  /** False outside the main app (the back office has no chat to ask in). */
  canAsk?: boolean
  showActionsInline?: boolean
  /** Shows "Add to collection" on each Topic card. False outside the main app. */
  canAddToCollection?: boolean
}

function errorMessage(error: unknown, fallback: string): string {
  return (
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback
  )
}

/**
 * The Library's Social listening tab: the posts meter and Topic cards. A card
 * opens its Topic through `onOpenUniverseChange`: the app's Topic page, or the
 * back office's drawer (`openUniverseToken`).
 */
export function SocialMediaTab(props: SocialMediaTabProps) {
  const {
    showHeader = true,
    universes,
    canManage,
    syncSchedule,
    isLoading,
    hasError = false,
    usage = null,
    usagePanel,
    openUniverseToken = null,
    onOpenUniverseChange,
    onUniverseChanged,
    onUniverseDeleted,
    onRefresh,
    onEditTopic,
    canHidePosts = canManage,
    showRunDiagnostics = false,
    canAsk = true,
    showActionsInline = false,
    canAddToCollection = false,
  } = props

  const { t, i18n } = useTranslation('app')
  const socialListeningApi = useSocialListeningApi()
  const { data: creditInfo } = useCreditInfo()
  const isOrgOwner = creditInfo?.isOrgOwner === true
  const refreshCredits = useRefreshCreditsOnEdge()

  const [busyToken, setBusyToken] = useState<string | null>(null)
  const [deletingUniverse, setDeletingUniverse] = useState<SocialUniverseItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const openUniverse = universes.find((universe) => universe.token === openUniverseToken) ?? null

  async function handleSync(universe: SocialUniverseItem) {
    setBusyToken(universe.token)
    try {
      const updated = await socialListeningApi.syncUniverse(universe.token)
      onUniverseChanged(updated)
      toast.success(t('socialListening.topicToast.updateStarted'))
      void refreshSocialPostsUsage()
      refreshCredits()
    } catch (error: unknown) {
      // Typed run-limit toasts (cooldown, queue full, out of credits), else the server message.
      const limit = socialLimitErrorToast(error, t, {
        isOrgOwner,
        contactSalesUrl: CONTACT_SALES_URL,
        language: i18n.language,
      })
      if (limit) {
        toast.error(limit.message, limit.action ? { action: limit.action } : undefined)
      } else {
        toast.error(errorMessage(error, t('socialListening.topicToast.actionFailed')))
      }
    } finally {
      setBusyToken(null)
    }
  }

  async function handleConfirmDelete() {
    if (!deletingUniverse) {
      return
    }
    const token = deletingUniverse.token
    setIsDeleting(true)
    try {
      await socialListeningApi.deleteUniverse(token)
      onUniverseDeleted(token)
      if (openUniverseToken === token) {
        onOpenUniverseChange(null)
      }
      toast.success(t('socialListening.topicToast.deleted'))
      setDeletingUniverse(null)
    } catch (error: unknown) {
      toast.error(errorMessage(error, t('socialListening.topicToast.actionFailed')))
    } finally {
      setIsDeleting(false)
    }
  }

  const body = isLoading ? (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] gap-[18px]">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-64 rounded-2xl" />
      ))}
    </div>
  ) : hasError && universes.length === 0 ? (
    <Text size="sm" color="muted" data-testid="social-topics-error">
      {t('socialListening.topicTab.loadFailed')}
    </Text>
  ) : universes.length === 0 ? (
    <div className="flex flex-col items-center gap-4 rounded-2xl border border-border/60 bg-muted/50 p-6 text-center dark:border-border dark:bg-card sm:p-10">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background text-muted-foreground dark:bg-muted">
        <Radio className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="flex max-w-lg flex-col items-center gap-2" data-testid="social-empty-state">
        <Heading level={3} className="font-sans text-lg font-medium">
          {t('socialListening.topicTab.emptyTitle')}
        </Heading>
        <Text size="sm" color="muted">
          {canManage
            ? t('socialListening.topicTab.emptyBody')
            : t('socialListening.topicTab.emptyBodyMember')}
        </Text>
      </div>
    </div>
  ) : (
    <div
      className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] gap-[18px]"
      data-testid="social-universe-list"
    >
      {universes.map((universe) => (
        <SocialUniverseCard
          key={universe.token}
          universe={universe}
          canManage={canManage}
          isBusy={busyToken === universe.token}
          syncSchedule={syncSchedule}
          canAsk={canAsk}
          showActionsInline={showActionsInline}
          canAddToCollection={canAddToCollection}
          onOpen={onOpenUniverseChange}
          onSync={(item) => void handleSync(item)}
          onEdit={onEditTopic}
          onDelete={setDeletingUniverse}
        />
      ))}
    </div>
  )

  const meter = usagePanel ?? <SocialUsageMeter usage={usage} />

  return (
    <section className="flex flex-col gap-4" data-testid="social-media-tab">
      {showHeader ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">{meter}</div>
        </div>
      ) : null}

      {body}

      <SocialUniverseDrawer
        universeToken={openUniverseToken}
        fallback={openUniverse}
        canManage={canManage}
        canHidePosts={canHidePosts}
        canAsk={canAsk}
        showRunDiagnostics={showRunDiagnostics}
        syncSchedule={syncSchedule}
        onClose={() => onOpenUniverseChange(null)}
        onEdit={onEditTopic}
        onUniverseChanged={onUniverseChanged}
        onUniverseDeleted={onUniverseDeleted}
        onRefresh={onRefresh}
      />

      <AlertDialog
        open={deletingUniverse != null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingUniverse(null)
          }
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('socialListening.topicDelete.title')}</AlertDialogTitle>
            <AlertDialogDescription data-testid="social-delete-body">
              {t('socialListening.topicDelete.body', { name: deletingUniverse?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="social-delete-cancel">
              {t('socialListening.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-destructive text-primary-dark-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              data-testid="social-delete-confirm"
              onClick={(event) => {
                event.preventDefault()
                void handleConfirmDelete()
              }}
            >
              {isDeleting
                ? t('socialListening.topicDelete.deleting')
                : t('socialListening.topicDelete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
